import { colors } from '@/styles';

export const MANIFEST_STATUSES = {
    NOT_RECEIVED: 'NOT_RECEIVED',
    RECEIVED: 'RECEIVED',
    AWAITING_STAGING: 'AWAITING_STAGING',
    IN_PROGRESS: 'IN_PROGRESS',
    STAGED: 'STAGED',
    COMPLETE: 'COMPLETE',
};

// Manifest type groupings (WILL_CALL is outbound here; warehouseManifests.js groups it as inbound).
export const OUTBOUND_MANIFEST_TYPES = ['OUTBOUND', 'RETURN_TO_SENDER', 'WILL_CALL'];
export const INBOUND_MANIFEST_TYPES = ['INBOUND', 'CROSS_DOCK'];

export const MANIFEST_STATUS_LABELS = {
    [MANIFEST_STATUSES.NOT_RECEIVED]: {
        label: 'Not Received',
        color: colors.reds.primary,
    },
    [MANIFEST_STATUSES.RECEIVED]: {
        label: 'Received',
        color: colors.greens.primary,
    },
    [MANIFEST_STATUSES.AWAITING_STAGING]: {
        label: 'Awaiting Staging',
        color: colors.reds.primary,
    },
    [MANIFEST_STATUSES.STAGED]: {
        label: 'Staged',
        color: colors.blues[0],
    },
    [MANIFEST_STATUSES.IN_PROGRESS]: {
        label: 'In Progress',
        color: colors.blues[0],
    },
    [MANIFEST_STATUSES.COMPLETE]: {
        label: 'Complete',
        color: colors.greens.primary,
    },
};

// Warehouse-facing chip labels for the manifest details header. Display only —
// the underlying status enum is shared with manifest_summary_view and must not change.
const STAGED_GREEN = { label: 'Staged', color: colors.greens.primary };

const MANIFEST_STATUS_DISPLAY_OVERRIDES = {
    INBOUND: {
        [MANIFEST_STATUSES.IN_PROGRESS]: {
            label: 'Receiving',
            color: colors.oranges.primary,
        },
    },
    CROSS_DOCK: {
        [MANIFEST_STATUSES.IN_PROGRESS]: {
            label: 'Receiving',
            color: colors.oranges.primary,
        },
    },
    // Fully staged is a "done" state for outbound — show it green rather than the shared blue.
    OUTBOUND: { [MANIFEST_STATUSES.STAGED]: STAGED_GREEN },
    RETURN_TO_SENDER: { [MANIFEST_STATUSES.STAGED]: STAGED_GREEN },
    WILL_CALL: { [MANIFEST_STATUSES.STAGED]: STAGED_GREEN },
};

export const getManifestStatusDisplay = (type, status) =>
    MANIFEST_STATUS_DISPLAY_OVERRIDES?.[type]?.[status] || MANIFEST_STATUS_LABELS?.[status];

export const PALLET_STATUSES = {
    RECEIVED: 'RECEIVED',
    STORED: 'STORED',
    PICKED: 'PICKED',
    STAGED: 'STAGED',
    PICKED_UP: 'PICKED_UP',
    NOT_RECEIVED: 'NOT_RECEIVED',
};

export const PALLET_STATUS_LABELS = {
    [PALLET_STATUSES.RECEIVED]: 'Received',
    [PALLET_STATUSES.STORED]: 'Stored',
    [PALLET_STATUSES.PICKED]: 'Picked',
    [PALLET_STATUSES.STAGED]: 'Staged',
    [PALLET_STATUSES.PICKED_UP]: 'Picked Up',
};

export const PALLET_STATUS_PROGRESSION = [
    PALLET_STATUSES.RECEIVED,
    PALLET_STATUSES.STORED,
    PALLET_STATUSES.PICKED,
    PALLET_STATUSES.STAGED,
    PALLET_STATUSES.PICKED_UP,
];

export const RECEIVED_STATUSES = [
    PALLET_STATUSES.RECEIVED,
    PALLET_STATUSES.STORED,
    PALLET_STATUSES.PICKED,
    PALLET_STATUSES.STAGED,
];

export const STORED_STATUSES = [
    PALLET_STATUSES.STORED,
    PALLET_STATUSES.PICKED,
    PALLET_STATUSES.STAGED,
    PALLET_STATUSES.PICKED_UP,
];

export const PICKED_STATUSES = [PALLET_STATUSES.PICKED, PALLET_STATUSES.STAGED, PALLET_STATUSES.PICKED_UP];

export const STAGED_STATUSES = [PALLET_STATUSES.STAGED, PALLET_STATUSES.PICKED_UP];

// "Return" isn't a manifests.type value: it's the INBOUND manifest the route-returns flow links to a
// route (returns at route submit, failed delivery attempts). Nothing else creates route-linked INBOUND
// manifests.
export const RETURN_MANIFEST_TYPE = 'RETURN';

export const isReturnManifest = (manifest) => manifest?.type === 'INBOUND' && !!manifest?.route_id;

export const manifestDisplayType = (manifest) => (isReturnManifest(manifest) ? RETURN_MANIFEST_TYPE : manifest?.type);

// Hasura filter for one Type filter option. RETURN and INBOUND split INBOUND on route_id so they don't overlap.
export const manifestTypeFilter = (type) => {
    if (type === RETURN_MANIFEST_TYPE) {
        return { type: { _eq: 'INBOUND' }, route_id: { _is_null: false } };
    }
    if (type === 'INBOUND') {
        return { type: { _eq: 'INBOUND' }, route_id: { _is_null: true } };
    }
    return { type: { _eq: type } };
};

export const MANIFEST_TYPE_LABELS = {
    [RETURN_MANIFEST_TYPE]: 'Return',
    INBOUND: 'Inbound',
    CROSS_DOCK: 'Cross-Dock',
    OUTBOUND: 'Outbound',
    RETURN_TO_SENDER: 'OS&D',
    WILL_CALL: 'Will Call',
};

export const MANIFEST_TYPE_TOOLTIPS = {
    [RETURN_MANIFEST_TYPE]: '(Receiving)',
    INBOUND: '(Receiving)',
    CROSS_DOCK: '(Receiving)',
    OUTBOUND: '(Deliveries)',
    RETURN_TO_SENDER: '(Overages, Shortages, and Damages)',
    WILL_CALL: '(Pickups)',
};
